import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useTheme, useMediaQuery, Box, Skeleton, Drawer } from '@mui/material';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import LayoutModal from '../../../components/LayoutModal';
import ModalTitle from '../../../components/common/ModalTitle';
import BaseButton from '../../common/BaseButton';
import useUsersFacade from '../../../store/profile/useUsersFacade';
import UpgradePlanButton from '../../Layout/components/UpgradePlanButton';
import api from '../../../utils/api';
import { useConfig } from '../../../Hooks/useConfig';
import { Warning2 } from 'iconsax-react';
import {
  OptionsContainer,
  OptionItem,
  OptionContent,
  CreditAmount,
  OptionPrice,
  SummarySection,
  SummaryRow,
  SummaryTextContainer,
  PurchasingLabel,
  PurchasingDetails,
  DetailItem,
  DetailLabel,
  DetailValue,
  DetailUnit,
  TotalPrice,
  OptionTextLabel,
  WarningContainer,
  WarningText,
  HeaderSubtitle,
  ExtraCreditsOneTimeWarning,
  WarningIconContainer,
  WarningContent,
} from './styles';

const BuyMoreCreditsModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const history = useHistory();
  const lgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const mdDown = useMediaQuery(theme.breakpoints.down('sm'));
  const { panelUrl } = useConfig();

  const { userInfo } = useUsersFacade();

  const isProPlanActive =
    userInfo?.payment?.plan_name?.toLowerCase().includes('pro') &&
    !userInfo?.payment?.plan_type?.includes('TRIAL');

  const isBasicPlanActive =
    userInfo?.payment?.plan_name?.toLowerCase().includes('basic') &&
    !userInfo?.payment?.plan_type?.includes('TRIAL');

  const isRestricted = !isProPlanActive && !isBasicPlanActive;

  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isButtonLoading, setIsButtonLoading] = useState(false);

  const isDarkMode = theme.palette.mode === 'dark';

  useEffect(() => {
    if (open) {
      fetchPlans();
    }
  }, [open]);

  const fetchPlans = async () => {
    setIsLoading(true);
    try {
      const plansListReq = await api.call({
        name: 'getMoreCreditsPlans',
      });

      if (plansListReq.status === 200) {
        const receivedPlans = plansListReq.data.data;
        // Sort plans by credits amount if needed, ensuring they are in order
        const sortedPlans = receivedPlans.sort((a, b) => a.credits - b.credits);
        setPlans(sortedPlans);

        // Select the middle plan by default, or the first one
        if (sortedPlans.length > 0) {
          // Try to select the second plan as default (3000 credits in original design), otherwise first
          setSelectedPlan(sortedPlans[1] || sortedPlans[0]);
        }
      }
    } catch (error) {
      console.error('failed to fetch plans');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (plan) => {
    if (isRestricted) return;
    setSelectedPlan(plan);
  };

  const handleBuyCredits = async () => {
    if (!selectedPlan || isRestricted) return;

    setIsButtonLoading(true);
    try {
      const addNewInvoiceReq = await api.call({
        name: 'addInvoice',
        data: {
          planId: selectedPlan.id,
        },
      });

      if (addNewInvoiceReq.status === 200) {
        const project = panelUrl.includes(window.location.hostname)
          ? 'panel'
          : 'playground';

        if (project === 'panel') {
          history.push(
            `/get-more-credits/payment/${addNewInvoiceReq.data.data.id}?plan-description=${selectedPlan.description}`
          );
        } else {
          window.location.href = `${panelUrl}/get-more-credits/payment/${addNewInvoiceReq.data.data.id}?plan-description=${selectedPlan.description}`;
        }
        onClose(); // Close modal after successful navigation
      }
    } catch (error) {
      console.error('Failed to create invoice', error);
      // Optionally handle error (toast, etc.)
    } finally {
      setIsButtonLoading(false);
    }
  };

  const renderContent = () => (
    <>
      <Box sx={{ mb: 2 }}>
        <ModalTitle
          sx={{
            mb: 1,
            textAlign: 'left',
            fontSize: { xs: '18px !important', md: '32px' },
          }}
        >
          {t('buy_more_credits.title', 'Buy More Credits')}
        </ModalTitle>
        <HeaderSubtitle>
          {t(
            'buy_more_credits.subtitle',
            'Choose the amount of credits you need to continue your work.'
          )}
        </HeaderSubtitle>
      </Box>

      {isRestricted && (
        <ExtraCreditsOneTimeWarning>
          <WarningIconContainer>
            <Warning2 size="24" color="#ECA62A" variant="Outline" />
          </WarningIconContainer>
          <WarningContent>
            Need extra credits? Credit purchase is only available on paid plans.
            Upgrade your plan to continue.
          </WarningContent>
        </ExtraCreditsOneTimeWarning>
      )}

      <OptionsContainer>
        {isLoading
          ? // Skeleton loading state
            Array.from(new Array(4)).map((_, index) => (
              <Skeleton
                key={index}
                variant="rectangular"
                height={60}
                sx={{ borderRadius: 2 }}
              />
            ))
          : plans.map((plan) => {
              const isSelected = selectedPlan?.id === plan.id;
              return (
                <OptionItem
                  key={plan.id}
                  $isSelected={isSelected}
                  $isDisabled={isRestricted}
                  onClick={() => handleSelect(plan)}
                >
                  {isSelected ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="10"
                        cy="10"
                        r="9"
                        stroke={
                          isRestricted
                            ? isDarkMode
                              ? '#555'
                              : '#ccc'
                            : isDarkMode
                              ? '#70c4f7'
                              : '#0486D7'
                        }
                        strokeWidth="2"
                      />
                      <circle
                        cx="10"
                        cy="10"
                        r="5"
                        fill={
                          isRestricted
                            ? isDarkMode
                              ? '#555'
                              : '#ccc'
                            : isDarkMode
                              ? '#70c4f7'
                              : '#0486D7'
                        }
                      />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="10"
                        cy="10"
                        r="9.5"
                        stroke={isDarkMode ? '#464646' : '#E5E5E5'}
                      />
                    </svg>
                  )}

                  <OptionContent>
                    <CreditAmount $isSelected={isSelected && !isRestricted}>
                      {plan.credits.toLocaleString()}
                    </CreditAmount>
                    <OptionTextLabel $isSelected={isSelected && !isRestricted}>
                      {t('common.credits', 'credits')}
                    </OptionTextLabel>
                  </OptionContent>

                  <OptionPrice $isSelected={isSelected && !isRestricted}>
                    ${plan.amount}
                  </OptionPrice>
                </OptionItem>
              );
            })}
      </OptionsContainer>

      <SummarySection>
        {selectedPlan && (
          <>
            <SummaryRow sx={isRestricted ? { gap: '12px' } : undefined}>
              {isRestricted ? (
                <UpgradePlanButton
                  userInfo={userInfo}
                  isCollapsed={false}
                  sx={{
                    width: 'calc(50% - 6px)',
                    // marginTop: '12px',
                    padding: '12px 16px !important',
                    fontSize: '14px !important',
                  }}
                />
              ) : (
                <SummaryTextContainer>
                  <PurchasingLabel>
                    {t('buy_more_credits.purchasing', "You're purchasing")}
                  </PurchasingLabel>
                  <PurchasingDetails>
                    <DetailItem>
                      <DetailLabel>{t('common.for', 'For')}</DetailLabel>
                    </DetailItem>
                    <DetailItem>
                      <DetailValue>
                        {selectedPlan.credits.toLocaleString()}
                      </DetailValue>
                      <DetailUnit>{t('common.credits', 'credits')}</DetailUnit>
                    </DetailItem>
                    <DetailItem>
                      <TotalPrice>
                        <span>{t('common.total', 'Total')}: </span>$
                        {selectedPlan.amount}
                      </TotalPrice>
                    </DetailItem>
                  </PurchasingDetails>
                </SummaryTextContainer>
              )}

              <BaseButton
                color="sky"
                size="small"
                onClick={handleBuyCredits}
                disabled={isButtonLoading || isLoading || isRestricted}
                loading={isButtonLoading}
                sx={{
                  width: isRestricted ? 'calc(50% - 6px)' : '154px',
                  height: '48px',
                  fontSize: '14px !important',
                  padding: '10px 16px !important',
                }}
              >
                {t('buy_more_credits.btn_buy', 'Buy Credits')}
              </BaseButton>
            </SummaryRow>
          </>
        )}
      </SummarySection>
    </>
  );

  if (mdDown) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            padding: theme.spacing(3),
            paddingTop: theme.spacing(1.5), // Reduce top padding to accommodate handle
            maxHeight: '85vh',
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark'
                ? theme.palette.secondary.surface_14
                : theme.palette.background.paper,
            backgroundImage: 'none',
          },
        }}
      >
        <Box
          sx={{
            width: '40px',
            height: '4px',
            backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#E0E0E0',
            borderRadius: '100px',
            margin: '0 auto 24px auto',
            opacity: 0.5,
          }}
        />
        {renderContent()}
      </Drawer>
    );
  }

  return (
    <LayoutModal
      open={open}
      handleClose={onClose}
      disabledIcon
      sx={{
        width: lgUp ? '500px' : '90%',
        maxWidth: '100%',
        padding: (theme) => theme.spacing(5, 3),
        overflow: 'hidden',
      }}
    >
      {renderContent()}
    </LayoutModal>
  );
};

BuyMoreCreditsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default BuyMoreCreditsModal;
